/**
 * TipTap Extension Bundle
 * Konfiguriert alle Extensions für den Rich-Text-Editor des Annotation-Systems.
 */

import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Youtube from '@tiptap/extension-youtube'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { ImageView } from './ImageView'
import { AnnotationLinkNode } from './AnnotationLinkNode'
import { AnnotationLinkView } from './AnnotationLinkView'
import { ViewSnapshotNode } from './ViewSnapshotNode'
import { ViewSnapshotView } from './ViewSnapshotView'
import { PaperlessDocLinkNode } from './PaperlessDocLinkNode'
import { PaperlessDocLinkView } from './PaperlessDocLinkView'
import { AuditLinkNode } from './AuditLinkNode'
import { AuditLinkView } from './AuditLinkView'
import { EmbedDragDrop } from './EmbedDragPlugin'
import { HeadingAnchor } from './HeadingAnchor'
import { SlashCommands, SlashCommandItem, getSlashQuery } from './SlashCommands'

export interface EditorExtensionsOptions {
  placeholder?: string
  onAnnotationClick?: (annotationId: string) => void
  getAnnotationColor?: (annotationId: string) => string
  onSnapshotClick?: (snapshotJson: string) => void
  onPaperlessDocClick?: (docId: number, title: string) => void
  onAuditClick?: (auditId: string) => void
  /** Slash-Commands: werden bei /scene, /audit etc. angeboten */
  slashCommands?: SlashCommandItem[]
}

export function buildEditorExtensions(options: EditorExtensionsOptions = {}) {
  const {
    placeholder = 'Text eingeben …',
    onAnnotationClick,
    getAnnotationColor,
    onSnapshotClick,
    onPaperlessDocClick,
    onAuditClick,
    slashCommands = [],
  } = options

  return [
    StarterKit.configure({
      // Heading-Ebenen 1–3
      heading: { levels: [1, 2, 3] },
      // Code-Blöcke aktivieren
      codeBlock: {},
      // Dropcursor: sichtbare Linie beim Drag-and-Drop
      dropcursor: {
        color: 'hsl(221 83% 53%)',
        width: 2,
        class: 'embed-dropcursor',
      },
    }),

    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),

    Image.extend({
      addNodeView() {
        return ReactNodeViewRenderer(ImageView)
      },
    }).configure({
      inline: false,
      allowBase64: true,  // Bilder direkt als Base64 einbetten
    }),

    Link.configure({
      openOnClick: true,
      autolink: true,
      HTMLAttributes: {
        rel: 'noopener noreferrer',
        style: 'color: hsl(221 83% 63%); text-decoration: underline;',
      },
    }),

    Youtube.configure({
      width: 480,
      height: 270,
      nocookie: true,
      allowFullscreen: true,
      HTMLAttributes: {
        style: 'border-radius: 0.375em; max-width: 100%;',
      },
    }),

    Placeholder.configure({
      placeholder,
      emptyEditorClass: 'is-editor-empty',
    }),

    // Custom: AnnotationLink mit React Node View
    AnnotationLinkNode.extend({
      addNodeView() {
        return ReactNodeViewRenderer(AnnotationLinkView)
      },
    }).configure({
      onAnnotationClick,
      getAnnotationColor,
    }),

    // Custom: ViewSnapshot mit React Node View
    ViewSnapshotNode.extend({
      addNodeView() {
        return ReactNodeViewRenderer(ViewSnapshotView)
      },
    }).configure({
      onSnapshotClick,
    }),

    // Custom: PaperlessDocLink mit React Node View
    PaperlessDocLinkNode.extend({
      addNodeView() {
        return ReactNodeViewRenderer(PaperlessDocLinkView)
      },
    }).configure({
      onDocClick: onPaperlessDocClick,
    }),

    // Custom: AuditLink mit React Node View
    AuditLinkNode.extend({
      addNodeView() {
        return ReactNodeViewRenderer(AuditLinkView)
      },
    }).configure({
      onAuditClick,
    }),

    // Custom Embed Drag-and-Drop (umgeht TipTap/ProseMirror Drag-Pipeline)
    EmbedDragDrop,

    // Auto-Slugs auf Überschriften (id={slug}) für Anker-Sprungmarken
    HeadingAnchor,

    // Slash-Commands: /scene, /audit, /doc, /notiz
    ...(slashCommands.length > 0 ? [SlashCommands.configure({ commands: slashCommands })] : []),
  ]
}

export { AnnotationLinkNode }
export { AnnotationLinkView }
export { ViewSnapshotNode }
export { ViewSnapshotView }
export { PaperlessDocLinkNode }
export { PaperlessDocLinkView }
export { AuditLinkNode }
export { AuditLinkView }
export { EmbedDragDrop }
export { HeadingAnchor, extractHeadings, slugify } from './HeadingAnchor'
export { SlashCommands, getSlashQuery }
export type { SlashCommandItem }
